import { z } from "zod";

/**
 * Shared DTO cho Admin Channel Health & Zalo OA/ZNS ops (US-AE7.F3-0024):
 *  - @UC9062 channel-health     — GET /admin/channels/health (delivery/fail-rate per kênh).
 *  - @UC9065 delivery-analytics — số liệu delivery (success/bounce/latency) + fail-rate alert flag.
 *  - @UC9063 zalo-zns-ops       — GET /admin/channels/zalo/templates (trạng thái duyệt + fallback).
 *
 * MỘT NGUỒN SỰ THẬT shape cho api (S01) + admin web (S03) — tránh drift DTO (DRY).
 *
 * BẢO MẬT / BẤT BIẾN (invariants):
 * - no-SMS (C1): enum kênh = in_app | push | zalo | email | teams — KHÔNG sms (khớp Notification.channel
 *   + OutboxChannel của F1-0023). Tái dùng `outboxChannelSchema` để 1 nguồn enum kênh.
 * - no-PII (C2): CHỈ số liệu tổng hợp vận hành + metadata template — KHÔNG payload/nội dung thông báo/PII.
 * - `.strict()` — reject field lạ tại biên.
 * - Fail-rate threshold = 0.05 (5%, story AC3). `alert` = failRate > 0.05 (expose cho AE9; task KHÔNG tự
 *   gửi alert). `fallbackRequired` = status ≠ approved (template chưa duyệt → cảnh báo fallback kênh khác).
 * - G2: DTO chỉ mô tả ĐỌC số liệu/trạng thái — KHÔNG field mutating ledger.
 */

import { outboxChannelSchema } from "./admin-outbox.js";

/** Ngưỡng fail-rate cảnh báo (5%, story AC3). failRate > ngưỡng → `alert=true`. */
export const CHANNEL_FAIL_RATE_ALERT_THRESHOLD = 0.05;

/** Trạng thái duyệt template ZNS phía Zalo OA. `approved` → không cần fallback. */
export const znsTemplateStatusSchema = z.enum(["pending", "approved", "rejected"]);
export type ZnsTemplateStatus = z.infer<typeof znsTemplateStatusSchema>;

/**
 * Số liệu sức khoẻ 1 kênh (@UC9062/@UC9065) — READ-ONLY, tổng hợp (KHÔNG PII).
 *  - `successRate`/`bounceRate`/`failRate` ∈ [0,1] (tỉ lệ, KHÔNG %). failRate = failed/total.
 *  - `latencyP50Ms`/`latencyP95Ms` — độ trễ dispatch (dispatchedAt−createdAt); null khi thiếu mẫu.
 *  - `total` — tổng số event kênh (in-app luôn đếm dù push tắt — event vẫn lưu để hiển thị).
 *  - `alert` = failRate > 0.05 (số liệu cho AE9; task KHÔNG tự gửi alert).
 */
export const channelHealthRowSchema = z
  .object({
    channel: outboxChannelSchema,
    total: z.number().int().min(0),
    dispatched: z.number().int().min(0),
    failed: z.number().int().min(0),
    pending: z.number().int().min(0),
    /**
     * 🔴 US-AE16.F5-0373-S01 (@UC9644) — event CHẾT HẲN (quá 5 lần retry, chờ admin replay tay).
     * Trước đây nó tồn tại trong enum + schema nhưng **không vào công thức nào**: `total` chỉ cộng
     * 3/4 trạng thái ⇒ `failRate` bị hạ thấp CÓ HỆ THỐNG, đúng ở kênh đang hỏng nặng nhất.
     * Nay vào **tử số** của `failRate` và **mẫu số** của mọi tỉ lệ.
     */
    deadLetter: z.number().int().min(0),
    /**
     * 🔴 Tư thế THẬT của adapter: `real` = đã nối nhà cung cấp · `noop` = **không có gì rời hệ thống**.
     * Nguồn là cấu hình đang chạy (`@goppy/config` `channelAdapterPosture`) — cùng predicate mà
     * worker dùng để CHỌN adapter, nên cờ không thể drift khỏi hành vi.
     * UI phải fail-closed: thiếu trường này (server cũ) ⇒ **hiện cảnh báo**, không im lặng.
     */
    adapter: z.enum(["real", "noop"]),
    successRate: z.number().min(0).max(1),
    bounceRate: z.number().min(0).max(1),
    failRate: z.number().min(0).max(1),
    latencyP50Ms: z.number().int().min(0).nullable(),
    latencyP95Ms: z.number().int().min(0).nullable(),
    alert: z.boolean(),
  })
  .strict();
export type ChannelHealthRow = z.infer<typeof channelHealthRowSchema>;

/** Envelope GET /admin/channels/health `{ data, window }`. window = mô tả khoảng thời gian (vd "all"). */
export const channelHealthResponseSchema = z
  .object({
    data: z.array(channelHealthRowSchema),
    window: z.string(),
  })
  .strict();
export type ChannelHealthResponse = z.infer<typeof channelHealthResponseSchema>;

/**
 * Một dòng trạng thái template ZNS (@UC9063) — READ-ONLY metadata (KHÔNG PII/nội dung/secret).
 *  - `fallbackRequired` = status ≠ approved (template chưa duyệt → cảnh báo dùng fallback in-app/email).
 *  - `eventType` — event nghiệp vụ dùng template (map cảnh báo); null nếu chưa gán.
 */
export const znsTemplateRowSchema = z
  .object({
    zaloTemplateId: z.string(),
    name: z.string(),
    status: znsTemplateStatusSchema,
    eventType: z.string().nullable(),
    fallbackRequired: z.boolean(),
    syncedAt: z.string().nullable(),
  })
  .strict();
export type ZnsTemplateRow = z.infer<typeof znsTemplateRowSchema>;

/** Envelope GET /admin/channels/zalo/templates `{ data }`. */
export const znsTemplatesResponseSchema = z
  .object({
    data: z.array(znsTemplateRowSchema),
  })
  .strict();
export type ZnsTemplatesResponse = z.infer<typeof znsTemplatesResponseSchema>;

/**
 * Kết quả POST /admin/channels/zalo/templates/:zaloTemplateId/refresh (@UC9063 mutation). Refresh =
 * đồng bộ/override thủ công trạng thái duyệt của 1 template (cập nhật syncedAt). Trả row sau refresh.
 * Mutation ATOMIC self-tx + audit (SUPER_ADMIN/OPS). Body optional `status` để override khi sync tay.
 */
export const znsTemplateRefreshRequestSchema = z
  .object({
    status: znsTemplateStatusSchema.optional(),
  })
  .strict();
export type ZnsTemplateRefreshRequest = z.infer<typeof znsTemplateRefreshRequestSchema>;

export const znsTemplateRefreshResponseSchema = znsTemplateRowSchema;
export type ZnsTemplateRefreshResponse = z.infer<typeof znsTemplateRefreshResponseSchema>;
